import React from 'react'
import { Formik, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import Input from '@material-ui/core/Input'
import MenuItem from '@material-ui/core/MenuItem'
import Chip from '@material-ui/core/Chip';
import { makeStyles } from "@material-ui/core/styles";

import LinearProgress from '@material-ui/core/LinearProgress';
import Alert from '@material-ui/lab/Alert';
import { Box, Typography } from '@material-ui/core';
import { Button, Form } from "../../../components/Wrappers";
import { useAddSnackbarQueue} from "../../../context/SnackbarContext";
import { useCloseDialog } from "../../../context/DialogContext";
import { useStoreHelpers } from '../../../store'
import {useApiHttpState} from '../../../context/ApiContext'
import { useHelperStyles } from '../../../styles'
import { useTheme } from '@material-ui/core';
import {transformNumberTo10DigitFormat} from '../../../utils/common'
import InviteLink from './InviteLink'

// Read an error message out of the [error, response] tuple the api context
// returns. The server sends the useful text in response.data.message.
const errorMessage = er =>
    (er && er.response && er.response.data && er.response.data.message) || (er && er.message) || 'Unknown error'

const useStyles = makeStyles((theme) => ({    
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      margin: 2,
    }    
  }));



const UpsertForm = ({user,roles}) => {    
    const close = useCloseDialog()    
    const {api} = useApiHttpState()
    const helperClasses = useHelperStyles()
    const chipClasses = useStyles()
    const theme = useTheme()
    const addSnackbarQueue = useAddSnackbarQueue()    
    
    const  { addItem, getItemsMap } = useStoreHelpers()  
    const rolesMap = getItemsMap("roles")
    // Set once a new user has been invited, which swaps the form out for the
    // link panel so the admin can copy it before closing the dialog.
    const [invite, setInvite] = React.useState(null)

    if (invite) {
        return (
            <Box>
                <InviteLink {...invite} />
                <Box mt={2}>
                    <Button variant="contained" color="primary" onClick={close}>Done</Button>
                </Box>
            </Box>
        )
    }

    return (
        <Formik
            initialValues={{
                firstName: user ? user.firstName : '',
                lastName:  user ? user.lastName : '', 
                cellphone: user ? user.cellphone : '',
                email:  user ? user.email : '', 
                roles: user && user.roles
                    ? user.roles.filter(role => role.name !== 'Appointment Handler').map(role => role.id)
                    : []
            }}
            validate={values => {
                const errors = {};
                if (!values.firstName) {
                    errors.firstName = 'Required';
                } 
                if (!values.lastName) {
                    errors.lastName = 'Required';
                }
                if (!values.email) {
                    errors.email = 'Required';
                }
                return errors;
            }}
            onSubmit={ async (values, { setSubmitting }) => {
                let newValues = {...values}
                newValues.cellphone = transformNumberTo10DigitFormat(values.cellphone)
                // New users go through the invitation endpoint: the account is
                // created active with these roles already assigned, and the
                // invitee sets their own password from the emailed link. No
                // separate approve-and-assign-roles step afterwards.
                const [er,response] = user
                    ? await api.put('/users/withRoles/'+user.id, {...newValues})
                    : await api.post('/users/invite', {...newValues})
                setSubmitting(false);                
                if (er) {
                    addSnackbarQueue("An error occurred " + errorMessage(er),"error")
                    return;
                }
                if (user) {
                    addItem(response.data,"users")
                    addSnackbarQueue("User was updated")
                    close()
                    return;
                }
                const { user: createdUser, inviteLink, emailSent, emailError } = response.data
                addItem(createdUser,"users")
                addSnackbarQueue(
                    emailSent
                        ? `${createdUser.email} was invited.`
                        : `${createdUser.email} was created but the invitation email failed to send.`,
                    emailSent ? "success" : "error",
                )
                // Stay open on the link panel instead of closing: the link is
                // the fallback when the email does not arrive.
                setInvite({ inviteLink, emailSent, emailError, email: createdUser.email })
                return;
            }}    
        >
        {({ submitForm, isSubmitting, values }) => (
            <Form>
                <Field
                    component={TextField}
                    name="firstName"
                    type="text"
                    label="First Name"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginRight}
                />
                
                <Field
                    component={TextField}
                    name="lastName"
                    type="text"
                    label="Last Name"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginLeft}
                />
                <Field
                    component={TextField}
                    name="cellphone"
                    type="text"
                    label="Cellphone"
                    variant="outlined"
                    margin="normal"
                    size="small"
                    className={helperClasses.marginLeft}
                />
                <br />
                <Field
                    component={TextField}
                    name="email"
                    type="email"
                    label="Email"
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    size="small"
                />
                <br /> 
                <Field
                    component={TextField}
                    select={true}
                    SelectProps={{
                        multiple: true,
                        renderValue : (selected) => (
                            <div className={chipClasses.chips}>
                              {selected.map((value) => (
                                <Chip key={value} label={rolesMap[value] ? rolesMap[value].name : ""} color="secondary" size="small" className={chipClasses.chip} />
                              ))}
                            </div>
                        )
                    }}
                    input={<Input id="select-multiple-chip" />}                    
                    name="roles"                    
                    label="Roles"
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    size="small"                    
                >
                    {roles.map((role) => (
                            <MenuItem key={role.id} value={role.id} style={{fontWeight: values.roles && values.roles.indexOf(role.id) == -1  ? theme.typography.fontWeightRegular
                                : theme.typography.fontWeightBold}}>
                                {role.name}
                            </MenuItem>
                    ))}
                </Field>
                
                             
                {!user &&
                    <Alert severity="info" className={helperClasses.marginTopBottom}>
                        <Typography variant="body2">
                            The account is created enabled with the roles above. An email invites
                            them to choose their own password - you never set it for them. You can
                            copy the link on the next screen if you would rather send it yourself.
                        </Typography>
                    </Alert>
                }

                {isSubmitting && <LinearProgress />}
                <br />
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    onClick={submitForm}
                >
                    {user ? "Submit" : "Create & Send Invitation"}
                </Button>
            </Form>
        )}
    </Formik>
    )
}

export default UpsertForm